import { useEffect, useState } from "react";
import {
  ImageList,
  ImageListItem,
  useMediaQuery,
  Skeleton,
  Typography,
  Box,
  Fab,
  alpha,
  Pagination,
  Paper,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import { default as VideoPlugin } from "yet-another-react-lightbox/plugins/video";
import "./Photos.css";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

const localPhotos = [
  {
    key: "https://as1.ftcdn.net/v2/jpg/02/20/80/30/500_F_220803033_x6Oci7G6zKKfjqgsl9gBn3dpOyLdZmTN.jpg",
  },
  {
    key: "https://brandingforthepeople.com/wp-content/uploads/2019/04/Stock-Photography-vs-Real-Imagery.jpg",
  },
  {
    key: "https://media.istockphoto.com/id/1487894858/photo/candlestick-chart-and-data-of-financial-market.jpg?s=612x612&w=0&k=20&c=wZ6vVmbm4BV2JOePSnNNz-0aFVOJZ0P9nhdeOMGUg5I=",
  },
  {
    key: "https://previews.123rf.com/images/gdolgikh/gdolgikh1504/gdolgikh150400087/38738606-group-of-happy-young-people-isolated-on-white-background.jpg",
  },
];

export default function Photos({ bucket_name, title }) {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const pagePhotos = photos.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const handleSetSelectedPhoto = (index) => {
    setPage(Math.floor(index / itemsPerPage) + 1);
    setSelectedPhoto(index);
  };

  const matches = useMediaQuery("(min-width:600px)");

  useEffect(() => {
    switch (process.env.NODE_ENV) {
      case "production":
        fetch(`/${bucket_name}/list`)
          .then((res) => res.json())
          .then((data) => {
            data = (data || [])
              .sort((a, b) => a.uploaded - b.uploaded)
              .map((value, index) => {
                const contentType = value.httpMetadata.contentType || "";
                if (contentType.startsWith("video/")) {
                  return {
                    type: "video",
                    src: encodeURI(`/${bucket_name}/${value.key}`),
                    sources: [
                      {
                        src: encodeURI(`/${bucket_name}/${value.key}`),
                        type: value.customMetadata.type,
                      },
                    ],
                    alt: value.key,
                    id: index,
                    loaded: true,
                  };
                }

                return {
                  type: "image",
                  src: encodeURI(`/${bucket_name}/${value.key}`),
                  alt: value.key,
                  id: index,
                  loaded: false,
                };
              });
            setPhotos(data);
          });
        break;

      default:
        setPhotos(
          localPhotos
            .concat(localPhotos)
            .concat(localPhotos)
            .concat(localPhotos)
            .concat(localPhotos)
            .concat(localPhotos)
            .concat(localPhotos)
            .concat(localPhotos)
            .concat(localPhotos)
            .map((value) => ({ value, sort: Math.random() }))
            .sort((a, b) => a.sort - b.sort)
            .map(({ value }, index) => ({
              type: "image",
              src: value.key,
              alt: value.key,
              id: index,
              loaded: false,
            }))
        );
    }
  }, [bucket_name]);

  return (
    <>
      <Typography align="center" variant="h3" color="secondary.main">
        {title}
      </Typography>
      <Lightbox
        plugins={[VideoPlugin]}
        open={selectedPhoto !== null}
        close={() => setSelectedPhoto(null)}
        slides={photos}
        index={selectedPhoto}
        on={{ view: ({ index }) => handleSetSelectedPhoto(index) }}
      />
      <Paper sx={{ p: 2, borderRadius: 4 }}>
        <ImageList variant="masonry" cols={matches ? 3 : 2} gap={16}>
          {pagePhotos.map((photo) => {
            switch (photo.type) {
              case "video":
                return (
                  <Video
                    key={photo.id}
                    video={photo}
                    onClick={() => setSelectedPhoto(photo.id)}
                  />
                );
              default:
                return (
                  <Image
                    key={photo.id}
                    photo={photo}
                    onClick={() => setSelectedPhoto(photo.id)}
                  />
                );
            }
          })}
        </ImageList>
        <Pagination
          sx={{
            display: "flex",
            justifyContent: "center",
            my: 4,
            color: "primary",
          }}
          count={Math.ceil(photos.length / itemsPerPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="secondary"
          size="large"
        />
      </Paper>
    </>
  );
}

export function Image({ photo, onClick, children }) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <ImageListItem className="imageHover" key={photo.id} onClick={onClick}>
      <img
        className="imageHover"
        key={photo.id}
        src={photo.src}
        alt={photo.alt}
        style={{
          borderRadius: 20,
          display: isLoaded ? "block" : "none",
        }}
        onLoad={() => setIsLoaded(true)}
      />
      {!isLoaded && (
        <Skeleton
          style={{ borderRadius: 20 }}
          variant="rectangular"
          height={300 + (Math.random() < 0.5 ? -1 : 1) * (Math.random() * 150)}
        />
      )}
      {children}
    </ImageListItem>
  );
}

export function Video({ video, onClick, children }) {
  return (
    <ImageListItem
      sx={{
        position: "relative",
        overflow: "hidden",
      }}
      className="imageHover"
      key={video.id}
      onClick={onClick}
    >
      <video
        src={video.src + "#t=0.1"}
        controls={false}
        style={{
          width: "100%",
          borderRadius: 20,
          display: "block",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
        }}
      >
        <Fab
          size="large"
          sx={(theme) => ({
            backgroundColor: alpha(theme.palette.secondary.main, 0.5),
            "&:hover": {
              backgroundColor: alpha(theme.palette.secondary.main, 0.8),
            },
          })}
        >
          <PlayArrow fontSize="large" />
        </Fab>
      </Box>
      {children}
    </ImageListItem>
  );
}
